import { COMMS_PREFERENCE_OPTIONS } from '@/constants/filterCriteria/commsPreferences';
import { CONTACT_STATUS_CODE_MAPPINGS } from '@/types';
import { PLACEHOLDER_EMAIL_FILTER_LABEL } from '@pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/hasEmail';
import { PLACEHOLDER_PHONE_FILTER_LABEL } from '@pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/hasPhone';
import { QueryBuilderFieldDeps } from '@pages/AdvancedSearchListCreatorPage/queryBuilder/types';

export function buildQueryBuilderFields(deps: QueryBuilderFieldDeps) {
  const { isOnTemplatePage, domainCode, pathwaySteps } = deps;
  const {
    lookupElections,
    lookupGroups,
    lookupLists,
    lookupGeometries,
    lookupMailings,
    lookupBranches,
    lookupTags,
  } = deps;

  return {
    age: {
      label: 'Age',
      type: 'age',
      operators: ['Equal to', 'Older than or equal to', 'Younger than or equal to'],
      defaultOperator: 'Equal to',
      fieldSettings: {
        useAsyncSearch: false,
      },
    },
    callingStatus: {
      label: 'Calling Status',
      type: 'callingStatus',
    },
    task: {
      label: 'Campaign tasks',
      type: 'task',
      fieldSettings: {
        lookupElections,
        domainCode,
        useAsyncSearch: false,
      },
    },
    commsPreferences: {
      label: 'Comms preferences',
      type: 'fetch',
      valueSources: ['value'],
      fieldSettings: {
        fetchLabel: 'Add a communication setting',
        preLoadedOptions: COMMS_PREFERENCE_OPTIONS,
        operatorInlineLabel: 'is one of',
        useAsyncSearch: false,
        templateBuilderDisplayName: 'Comms preferences filter',
        isOnTemplatePage,
      },
    },
    hasEmail: {
      label: 'Contactable by email',
      type: 'select',
      valueSources: ['value'],
      fieldSettings: {
        listValues: [
          { value: 'yes', title: 'Yes' },
          { value: 'no', title: 'No' },
        ],
        operatorInlineLabel: 'is emailable',
        defaultValue: 'yes',
        templateBuilderDisplayName: PLACEHOLDER_EMAIL_FILTER_LABEL,
        allowCustomValues: true,
      },
    },
    hasPhone: {
      label: 'Contactable by phone',
      type: 'select',
      valueSources: ['value'],
      fieldSettings: {
        listValues: [
          { value: 'any', title: 'Any' },
          { value: 'mobile', title: 'Mobile' },
          { value: 'landline', title: 'Landline' },
        ],
        operatorInlineLabel: 'has phone type',
        defaultValue: 'any',
        templateBuilderDisplayName: PLACEHOLDER_PHONE_FILTER_LABEL,
        allowCustomValues: true,
      },
    },
    engagementHistory: {
      label: 'Engagement History',
      type: 'engagementHistory',
    },
    eventAttendance: {
      label: 'Event Attendance',
      type: 'eventAttendance',
    },
    groupIds: {
      label: 'Group',
      type: 'fetch',
      valueSources: ['value'],
      fieldSettings: {
        asyncFetch: lookupGroups,
        fetchLabel: 'Add group(s)',
        operatorInlineLabel: 'is one of',
        isOnTemplatePage,
        templateBuilderDisplayName: 'Group filter',
      },
    },
    listIds: {
      label: 'List',
      type: 'fetch',
      valueSources: ['value'],
      fieldSettings: {
        asyncFetch: lookupLists,
        fetchLabel: 'Add list(s)',
        templateBuilderDisplayName: 'List filter',
        isOnTemplatePage,
      },
    },
    geometryIds: {
      label: 'Location',
      type: 'operatorFetch',
      valueSources: ['value'],
      operators: ['lives in', 'lives or placement in', 'placement in'],
      defaultOperator: 'lives in',
      fieldSettings: {
        asyncFetch: lookupGeometries,
        fetchLabel: 'Add location(s)',
        isOnTemplatePage,
        templateBuilderDisplayName: 'Location filter',
      },
    },
    mailing: {
      label: 'Mailing',
      type: 'mailing',
      fieldSettings: {
        lookupMailings,
        useAsyncSearch: false,
      },
    },
    membership: {
      label: 'Membership status',
      type: 'membership',
      fieldSettings: {
        lookupBranches,
        domainCode,
        useAsyncSearch: false,
      },
    },
    supporterStatuses: {
      label: 'Rocket status',
      type: 'fetch',
      valueSources: ['value'],
      fieldSettings: {
        fetchLabel: 'Find a Rocket status',
        preLoadedOptions: Object.entries(CONTACT_STATUS_CODE_MAPPINGS).map(([key, val]) => ({
          id: key,
          label: val,
        })),
        operatorInlineLabel: 'is one of',
        useAsyncSearch: false,
        templateBuilderDisplayName: 'Rocket status filter',
        isOnTemplatePage,
      },
    },
    roster: {
      label: 'Roster',
      type: 'roster',
    },
    sa1: {
      label: 'Statistical Area 1',
      type: 'idArray',
      valueSources: ['value'],
      fieldSettings: {
        operatorInlineLabel: 'is one of',
        useAsyncSearch: false,
        templateBuilderDisplayName: 'Statistical area 1 filter',
      },
    },
    tagIds: {
      label: 'Tag',
      type: 'fetch',
      valueSources: ['value'],
      fieldSettings: {
        asyncFetch: lookupTags,
        fetchLabel: 'Add tag(s)',
        operatorInlineLabel: 'is one of',
        isOnTemplatePage,
        templateBuilderDisplayName: 'Tag filter',
      },
    },
    pathwaySteps: {
      label: 'Volunteer Pathway',
      type: 'fetch',
      valueSources: ['value'],
      fieldSettings: {
        fetchLabel: 'Step(s)',
        preLoadedOptions: pathwaySteps ?? [],
        operatorInlineLabel: 'is one of',
        useAsyncSearch: false,
        isOnTemplatePage,
        templateBuilderDisplayName: 'Volunteer pathway filter',
      },
    },
  };
}
